'use client';

import { createContext, useContext, useState, useCallback, ReactNode, useMemo } from 'react';
import { Message } from './ChatWidget.interfaces';
import { useChatLogic } from './hooks/useChatLogic';

interface ChatContextValue {
  // Widget state
  isOpen: boolean;
  toggleChat: () => void;

  // Chat state (frequently changing)
  messages: Message[];
  isTyping: boolean;
  conversationId: string | null;

  // Actions (stable functions)
  sendMessage: (message: string) => Promise<void>;
}

export const ChatContext = createContext<ChatContextValue | null>(null);

interface ChatProviderProps {
  children: ReactNode;
}

/**
 * Unified chat context that provides both widget state and chat functionality
 * - Widget state: isOpen, toggleChat (for external components)
 * - Chat state: messages, isTyping, conversationId (changes frequently)
 * - Actions: sendMessage, handleLike, etc. (stable functions)
 */
export function ChatProvider({ children }: ChatProviderProps) {
  const [isOpen, setIsOpen] = useState(false);

  const toggleChat = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // Get chat functionality - only initialize when chat is open
  const { messages, conversationId, isTyping, sendMessage } = useChatLogic(isOpen);

  // Memoize the complete context value
  const contextValue = useMemo(
    () => ({
      // Widget state
      isOpen,
      toggleChat,

      // Chat state
      messages,
      isTyping,
      conversationId,

      // Actions
      sendMessage,
    }),
    [isOpen, toggleChat, messages, isTyping, conversationId, sendMessage],
  );

  return <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>;
}

/**
 * Hook to access complete chat context
 * Must be used within ChatProvider
 */
export function useChatContext() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within ChatProvider');
  }
  return context;
}

// Convenience hooks for specific needs (optional)
export function useChatState() {
  const { messages, isTyping, conversationId } = useChatContext();
  return { messages, isTyping, conversationId };
}

export function useChatActions() {
  const { sendMessage } = useChatContext();
  return { sendMessage };
}

export function useChatWidget() {
  const { isOpen, toggleChat } = useChatContext();
  return { isOpen, toggleChat };
}
